import {
  SUPPORTED_CHAT_TOOLS
} from 'common/constants/constants';
import { SettingValues } from '@linearb/linjs-common';
import { CollaborationSetting, Collaborations, collaborationTypeToSettingName } from './collaborationSettings.types';
import { calcDefaultNotifications, calcDefaultSettings, convertValuesObjectToArray } from './collaborationSettings.utils';

/**
 * Get the org settings for a specific org and collaboration type using the settings and teams
 * @returns Org settings
 */
export const getSettings = ({ allSettings, allTeams, collaborationType }: {
   allSettings: any, allTeams: any, collaborationType: Collaborations }) => {

    if (allSettings.length === 0 || allTeams === 0) {
      return [];
    }

  const collaborationSettingName =
    collaborationTypeToSettingName[collaborationType];
  const teamCollaborationChannelConfigId = allSettings.filter(
    setting => setting.name === collaborationSettingName
  )?.[0]?.id;

  const allTeamsWithCollaboration = allSettings.filter(
    setting => setting.definitionId === teamCollaborationChannelConfigId
  );
  const allTeamsWithCollaborationIds = allTeamsWithCollaboration.map(
    team => team.teamId
  );

  const defaultSettings = calcDefaultSettings({ allSettings });
  const defaultSettingsIds = defaultSettings.map(setting => setting.id);

  const defaultNotifications = calcDefaultNotifications({ defaultSettings });
  const defaultNotificationValues = Object.values(defaultNotifications);

  const notificationEnableSetting = allSettings.filter(
    setting => setting.name === SettingValues.TEAM_IS_NOTIFICATIONS_ENABLED
  )?.[0];

  const teamIds = allTeams.map(team => team.id).sort((a, b) => a - b);
  const orgSettings = teamIds.reduce((acc: object, teamId: number) => {
    const isTeamWithDetails = allTeamsWithCollaborationIds.includes(teamId);

    acc[teamId] = getSettingsForTeam({
      teamId,
      isTeamWithDetails,
      collaborationType,
      teamCollaborationChannelConfigId,
      allSettings,
      defaultSettingsIds,
      notificationEnableSetting,
      defaultNotifications,
      defaultNotificationValues
    });
    return acc;
  }, {});

  return orgSettings;
};

/**
 * Get setting for team, checks if this is team that we need to fill the data (has collaboration) or can take the default values
 * @returns Settings object
 */
const getSettingsForTeam = ({
  teamId,
  isTeamWithDetails,
  collaborationType,
  teamCollaborationChannelConfigId,
  allSettings,
  defaultSettingsIds,
  notificationEnableSetting,
  defaultNotifications,
  defaultNotificationValues
}: CollaborationSetting) => {
  if (!isTeamWithDetails) {
    return {
      slackChannel: null,
      msTeamsChannel: null,
      isNotificationEnabled: notificationEnableSetting.defaultValue,
      teamId,
      notifications: [...defaultNotificationValues]
    };
  } else {
    return getTeamInfoAndNotifications({
      teamId,
      collaborationType,
      teamCollaborationChannelConfigId,
      allSettings,
      defaultSettingsIds,
      notificationEnableSetting,
      defaultNotifications
    });
  }
};

/**
 * Get the teams info for teams that has collaboration connectivity
 * @returns Team info object
 */
const getTeamInfoAndNotifications = ({
  teamId,
  collaborationType,
  teamCollaborationChannelConfigId,
  allSettings,
  defaultSettingsIds,
  notificationEnableSetting,
  defaultNotifications
}: CollaborationSetting) => {
  let slackChannel = null;
  let msTeamsChannel = null;

  const channelDetails = allSettings.filter(
    setting =>
    setting.definitionId === teamCollaborationChannelConfigId && setting.teamId === teamId
  )?.[0]?.value;
  if (collaborationType === SUPPORTED_CHAT_TOOLS.SLACK) {
    slackChannel = channelDetails;
  } else {
    msTeamsChannel = channelDetails;
  }

  const isNotificationEnabledValue = allSettings.filter(
    setting => setting.definitionId === notificationEnableSetting.id && setting.teamId === teamId
  );
  const isNotificationEnabled = isNotificationEnabledValue.length
    ? isNotificationEnabledValue[0].value
    : notificationEnableSetting.defaultValue;

  const teamSettings = allSettings.filter(
    setting => setting.teamId === teamId && defaultSettingsIds.includes(setting.definitionId)
  );

  const teamNotifications = structuredClone(defaultNotifications);
  teamSettings.forEach(({ definitionId: id, value }) => {
    const { enabled, ...restOfValue } = value;
    teamNotifications[id].enabled = enabled;
    teamNotifications[id].values = convertValuesObjectToArray(restOfValue);
  });

  const notifications = Object.values(teamNotifications);

  return {
    slackChannel,
    msTeamsChannel,
    isNotificationEnabled,
    teamId,
    notifications
  };
};
